import React, { FC } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { scale as initialScale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { ReadyPcsV2Props } from '@components/common/ReadyPcsV2';
import { useManageReadyPcsV2BulletAnimation } from '@components/common/ReadyPcsV2/hooks/useManageReadyPcsV2BulletAnimation';
import { DEFAULT_MIN_BULLETS_FOR_WIDTH } from '@components/common/ReadyPcsV2/constants';

interface ReadyPcsBulletsProps
  extends Pick<ReadyPcsV2Props, 'activeBulletConfig' | 'C' | 'quantityProduced' | 'quantityTarget' | 'scale' | 'minBulletsForWidth'> {}

const ReadyPcsBullets: FC<ReadyPcsBulletsProps> = ({
  quantityProduced,
  quantityTarget,
  C,
  activeBulletConfig,
  scale = initialScale,
  minBulletsForWidth = DEFAULT_MIN_BULLETS_FOR_WIDTH,
}) => {
  const {
    amountOfBullets,
    shouldRenderBullets,
    getBulletBg,
    progressFlex,
    remainingFlex,
    timelineBackgroundColor,
    finishedBulletColor,
    animatedStyle,
    shouldRenderAnimatedBullet,
    activeBulletStyle,
    animationColor,
    animatedBulletIndex,
  } = useManageReadyPcsV2BulletAnimation({ quantityProduced, quantityTarget, activeBulletConfig });

  const commonStyles = {
    borderRadius: scale(3),
    height: scale(6),
  };

  // Should we add an extra animated bullet at the end (overlap case)
  const shouldAddExtraAnimatedBullet = shouldRenderAnimatedBullet && animatedBulletIndex === null;

  if (shouldRenderBullets) {
    const totalVisibleBullets = amountOfBullets + (shouldAddExtraAnimatedBullet ? 1 : 0);
    const invisiblePlaceholdersCount = Math.max(0, minBulletsForWidth - totalVisibleBullets);

    return (
      <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', gap: scale(3) }}>
        {Array.from({ length: amountOfBullets }).map((_, index) => {
          const isAnimatedBullet = animatedBulletIndex === index;

          if (isAnimatedBullet && animationColor) {
            return (
              <Animated.View
                key={index}
                style={[commonStyles, { flex: 1 }, activeBulletStyle, animatedStyle, { backgroundColor: C(animationColor) }]}
              />
            );
          }

          return <View key={index} style={[commonStyles, { flex: 1, backgroundColor: C(getBulletBg(index)) }]} />;
        })}
        {shouldAddExtraAnimatedBullet && animationColor && (
          <Animated.View style={[commonStyles, { flex: 1 }, activeBulletStyle, animatedStyle, { backgroundColor: C(animationColor) }]} />
        )}
        {Array.from({ length: invisiblePlaceholdersCount }).map((_, index) => (
          <View key={`placeholder-${index}`} style={{ flex: 1 }} />
        ))}
      </View>
    );
  }

  const animatedBarWidth = scale(5);

  return (
    <View style={[commonStyles, { width: '100%', flexDirection: 'row', backgroundColor: C(timelineBackgroundColor) }]}>
      <View style={{ flex: progressFlex, flexDirection: 'row', alignItems: 'center' }}>
        {shouldRenderAnimatedBullet && animationColor && (
          <Animated.View
            style={[
              commonStyles,
              {
                position: 'absolute',
                right: -animatedBarWidth,
                width: commonStyles.height + animatedBarWidth,
                backgroundColor: C(animationColor),
                zIndex: -1,
              },
              activeBulletStyle,
              animatedStyle,
            ]}
          />
        )}
        <View style={[commonStyles, { flex: 1, backgroundColor: C(finishedBulletColor) }]} />
      </View>
      <View style={{ flex: remainingFlex }} />
    </View>
  );
};

export default ColorWrapper(ReadyPcsBullets);
